/**
 * Shared flat-shaded mesh accumulator for the roof generators (extracted from
 * generate3d.ts in ROOF ROUND E2 so the framing generator reuses it without a
 * generate3d ↔ framing import cycle). Triangle normals come from winding.
 */
export interface MeshAcc {
  pos: number[]
  nrm: number[]
  idx: number[]
  /** GRAIN-ALIGNED FRAMING round: per-vertex (u,v) in world feet, populated ONLY
   *  when every pushTri call for this accumulator supplies `uvOf` (`prism()`'s
   *  member-local axis) — a partially-populated array is never emitted (see the
   *  length guard at each finalization site), so an accumulator mixing tagged and
   *  untagged pushes safely falls back to the generic world-projection pass. */
  uv: number[]
  vc: number
}

export function newMeshAcc(): MeshAcc {
  return { pos: [], nrm: [], idx: [], uv: [], vc: 0 }
}

export function pushTri(
  m: MeshAcc, a: number[], b: number[], c: number[],
  uvOf?: (p: number[]) => [number, number],
): void {
  // flat-shaded triangle, normal from winding
  const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2]
  const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
  let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
  const len = Math.hypot(nx, ny, nz) || 1
  nx /= len; ny /= len; nz /= len
  m.pos.push(...a, ...b, ...c)
  m.nrm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz)
  m.idx.push(m.vc, m.vc + 1, m.vc + 2)
  if (uvOf) {
    const ua = uvOf(a), ub = uvOf(b), uc = uvOf(c)
    m.uv.push(ua[0], ua[1], ub[0], ub[1], uc[0], uc[1])
  }
  m.vc += 3
}

/** Finalize an accumulator's uv channel, iff EVERY push in it carried one
 *  (the partial-population guard — see MeshAcc.uv above). */
export function meshAccUvs(m: MeshAcc): Float32Array | undefined {
  return m.uv.length === (m.pos.length / 3) * 2 && m.uv.length > 0 ? new Float32Array(m.uv) : undefined
}
